import dynamic from 'next/dynamic';

import { cn } from '@natu/utils/cn';
import { getSlugWithDash } from '@natu/utils/getSlugWithDash';

import {
  getAssetFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  SBProps,
  Spacing,
  StoryblokAsset,
} from '../../../../../utils';
import { ArticleCardButton } from './ArticleCardButton';

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

interface SBArticleCardProps {
  article?: {
    name?: string;
    content?: {
      previewImage?: StoryblokAsset;
      shortDescription?: string;
      description?: string;
    };
    full_slug?: string;
  };
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
}

export const SBArticleCard = ({ blok }: SBProps<SBArticleCardProps>) => {
  const { article, mbDesktop, mbMobile, mbTablet, mtDesktop, mtMobile, mtTablet } = blok;

  if (!article) {
    return null;
  }

  const { name, content, full_slug } = article;

  const articleImage =
    content?.previewImage &&
    getAssetFromStoryblok(content.previewImage as StoryblokAsset, { type: 'image' });

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const description = content?.shortDescription || content?.description;

  return (
    <div className={cn(className, '@container overflow-hidden rounded-2xl')} {...sbEditable(blok)}>
      <div className="@xl:grid-cols-2 grid grid-cols-1">
        <div className="bg-warning-25 flex h-full flex-col items-start justify-center p-6">
          <p className="text-xl font-bold text-gray-900 xl:text-2xl">{name}</p>
          {description && (
            <p className="mt-2 line-clamp-2 text-sm text-gray-700 xl:text-base">{description}</p>
          )}
          <ArticleCardButton articleLink={getSlugWithDash(full_slug)} />
        </div>
        <div className="overflow-hidden">
          {articleImage?.src && (
            <ResponsiveImage className="h-full object-cover" {...articleImage} />
          )}
        </div>
      </div>
    </div>
  );
};
